import {
  faDownload,
  faMinus,
  faPlus,
  faStar,
  faThumbtack
} from '@fortawesome/free-solid-svg-icons'
import {
  faArrowsMaximize,
  faArrowsMinimize,
  faFile,
  faImageSlash
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Dropdown } from '~/components/Form'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { useConfig } from '~/domains/configuration/queries/config'
import { useResumePin } from '~/hooks/useResumePin'
import { themeClass } from '~/styles/theme.css'
import type { AttachmentType } from '../types'
import { detectAttachmentFormat } from '../utils/formatDetector'
import {
  attachmentsViewer,
  attachmentsViewerContent,
  attachmentsViewerFullscreen,
  attachmentsViewerToolbar,
  attachmentsViewerToolbarDropdown,
  attachmentsViewerToolbarItem,
  attachmentsViewerToolbarItemActionnable,
  attachmentsViewerToolbarItemDisabled,
  attachmentsViewerToolbarItemPinned,
  attachmentsViewerToolbarItemScale,
  attachmentsViewerToolbarName,
  attachmentsViewerToolbarNameIcon,
  attachmentsViewerToolbarNameText,
  attachmentsViewerToolbarOption,
  noAttachments,
  noAttachmentsIcon
} from './AttachmentsViewer.css'
import { DocumentViewer } from './viewers/DocumentViewer'
import { HtmlViewer } from './viewers/HtmlViewer'
import { ImageViewer } from './viewers/ImageViewer'
import { PdfViewer } from './viewers/PdfViewer'
import { TextViewer } from './viewers/TextViewer'
import { UnavailableViewer } from './viewers/UnavailableViewer'
import { UnsupportedViewer } from './viewers/UnsupportedViewer'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

export const AttachmentsViewer = ({
  attachments,
  defaultAttachmentId,
  isLoading,
  defaultScale,
  showPinButton,
  groupByType = false,
  onAttachmentChange,
  toolbarActions
}: {
  attachments: AttachmentType[]
  defaultAttachmentId?: AttachmentType['id']
  isLoading: boolean
  defaultScale?: number
  showPinButton?: boolean
  groupByType?: boolean
  onAttachmentChange?: (id: AttachmentType['id']) => void
  toolbarActions?: ReactNode
}) => {
  const [selectedAttachment, setSelectedAttachment] = useState<
    AttachmentType['id'] | null
  >(defaultAttachmentId ?? null)
  const [numPages, setNumPages] = useState<number>()
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [scale, setScale] = useState(defaultScale ?? 1)

  const previewRef = useRef<HTMLDivElement>(null)
  const { data: legacyUrl } = useConfig('organization.legacy_url')
  const { isPinned, togglePin } = useResumePin()

  useEffect(() => {
    if (defaultAttachmentId) {
      setSelectedAttachment(defaultAttachmentId)
      onAttachmentChange?.(defaultAttachmentId)
    }
  }, [defaultAttachmentId, onAttachmentChange])

  useEffect(() => {
    const fullscreenListener = () => {
      const isFs = !!document.fullscreenElement
      setIsFullScreen(isFs)
      document.body.classList.toggle('fullscreen', isFs)
    }
    document.addEventListener('fullscreenchange', fullscreenListener)
    return () => {
      document.removeEventListener('fullscreenchange', fullscreenListener)
      document.body.classList.remove('fullscreen')
      // Guard: exitFullscreen rejects when the document is not active
      if (document.fullscreenElement && document.exitFullscreen) {
        void document.exitFullscreen().catch(() => {})
      }
    }
  }, [])

  const handleWheel = useCallback((e: WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault()
      const delta = e.deltaY < 0 ? 0.1 : -0.1
      setScale((prev) => Math.max(0.1, Math.round((prev + delta) * 10) / 10))
    }
  }, [])

  useEffect(() => {
    const el = previewRef.current
    if (!el) return
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  const dropdownOptions = useMemo(() => {
    const buildOption = (attachment: AttachmentType) => ({
      label: attachment.name,
      value: String(attachment.id),
      element: (
        <span className={attachmentsViewerToolbarOption}>
          {attachment.isDefaultResume && (
            <FontAwesomeIcon
              icon={faStar}
              className={attachmentsViewerToolbarNameIcon}
            />
          )}
          <FontAwesomeIcon
            icon={attachment.formatInfo?.icon ?? faFile}
            className={attachmentsViewerToolbarNameIcon}
          />
          <span className={attachmentsViewerToolbarNameText}>
            {attachment.name}
          </span>
        </span>
      )
    })

    if (!groupByType) {
      return attachments.map(buildOption)
    }

    const groups = new Map<string, AttachmentType[]>()
    for (const attachment of attachments) {
      const key = attachment.type
      const existing = groups.get(key)
      if (existing) {
        existing.push(attachment)
      } else {
        groups.set(key, [attachment])
      }
    }

    if (groups.size <= 1) {
      return attachments.map(buildOption)
    }

    return Array.from(groups.entries()).flatMap(([type, items]) => [
      { label: type, value: `group_${type}`, group: true as const },
      ...items.map(buildOption)
    ])
  }, [attachments, groupByType])

  if (isLoading) {
    return <Shimmer height={400} />
  }

  attachments.forEach((attachment) => {
    attachment.formatInfo =
      attachment.formatInfo ?? detectAttachmentFormat(attachment.name)
  })

  const attachmentData = attachments.find(
    (attachment) => attachment.id === selectedAttachment
  )

  if (!attachmentData) {
    return (
      <div className={clsx(noAttachments, themeClass)}>
        <div className={noAttachmentsIcon}>
          <FontAwesomeIcon icon={faImageSlash} />
        </div>
        Aucun document disponible
      </div>
    )
  }

  const formatInfo =
    attachmentData.formatInfo ?? detectAttachmentFormat(attachmentData.name)

  const downloadUrl =
    attachmentData?.url?.indexOf('/admin') !== -1
      ? `${legacyUrl}${attachmentData?.url}`
      : attachmentData?.url

  return (
    <div
      className={clsx(
        attachmentsViewer,
        themeClass,
        isFullScreen ? attachmentsViewerFullscreen : null
      )}
      ref={previewRef}
    >
      {(numPages || formatInfo) && (
        <div className={attachmentsViewerToolbar}>
          {attachments.length === 1 && (
            <span className={attachmentsViewerToolbarName}>
              <FontAwesomeIcon
                icon={formatInfo?.icon ?? faFile}
                className={attachmentsViewerToolbarNameIcon}
              />
              <span className={attachmentsViewerToolbarNameText}>
                {attachmentData.name}
              </span>
            </span>
          )}
          {attachments.length > 1 && (
            <Dropdown
              small
              required
              wrapperClassName={attachmentsViewerToolbarDropdown}
              options={dropdownOptions}
              defaultValueOption={{
                label: attachmentData.name,
                value: String(selectedAttachment),
                element: (
                  <span className={attachmentsViewerToolbarName}>
                    <FontAwesomeIcon
                      icon={attachmentData.formatInfo?.icon ?? faFile}
                      className={attachmentsViewerToolbarNameIcon}
                    />
                    <span className={attachmentsViewerToolbarNameText}>
                      {attachmentData.name}
                    </span>
                  </span>
                )
              }}
              onChange={(value) => {
                const id = Number(value)
                setSelectedAttachment(id)
                onAttachmentChange?.(id)
              }}
            />
          )}
          {formatInfo?.supportsZoom && (
            <span className={attachmentsViewerToolbarItem}>
              <span
                className={attachmentsViewerToolbarItemActionnable}
                onClick={(e) => {
                  e.preventDefault()
                  setScale((scale) => Math.max(scale - 0.1, 0.1))
                }}
              >
                <FontAwesomeIcon icon={faMinus} />
              </span>
              <span
                onClick={(e) => {
                  e.preventDefault()
                  setScale(1)
                }}
                className={clsx(
                  attachmentsViewerToolbarItemScale,
                  scale === 1
                    ? attachmentsViewerToolbarItemDisabled
                    : attachmentsViewerToolbarItemActionnable
                )}
              >
                {Math.floor(scale * 100)}%
              </span>
              <span
                className={attachmentsViewerToolbarItemActionnable}
                onClick={(e) => {
                  e.preventDefault()
                  setScale((scale) => scale + 0.1)
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
              </span>
            </span>
          )}

          {toolbarActions}

          {attachmentData.url && (
            <Tooltip text="Télécharger le document" direction="top">
              <span
                className={clsx(
                  attachmentsViewerToolbarItem,
                  attachmentsViewerToolbarItemActionnable
                )}
                onClick={(e) => {
                  e.preventDefault()
                  window.open(downloadUrl)
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
              </span>
            </Tooltip>
          )}

          <Tooltip
            text={isFullScreen ? 'Réduire' : 'Plein écran'}
            direction="top"
          >
            <span
              className={clsx(
                attachmentsViewerToolbarItem,
                attachmentsViewerToolbarItemActionnable
              )}
              onClick={(e) => {
                e.preventDefault()
                if (!document.fullscreenElement) {
                  void document.documentElement
                    .requestFullscreen()
                    .catch(() => {})
                } else if (document.exitFullscreen) {
                  void document.exitFullscreen().catch(() => {})
                }
              }}
            >
              <FontAwesomeIcon
                icon={isFullScreen ? faArrowsMinimize : faArrowsMaximize}
              />
            </span>
          </Tooltip>

          {showPinButton && (
            <Tooltip
              text={isPinned ? 'Désépingler' : 'Épingler'}
              direction="top"
            >
              <span
                className={clsx(
                  attachmentsViewerToolbarItem,
                  attachmentsViewerToolbarItemActionnable,
                  isPinned ? attachmentsViewerToolbarItemPinned : null
                )}
                onClick={(e) => {
                  e.preventDefault()
                  togglePin()
                }}
              >
                <FontAwesomeIcon icon={faThumbtack} />
              </span>
            </Tooltip>
          )}
        </div>
      )}
      <div className={attachmentsViewerContent}>
        {attachmentData.unavailable ? (
          <UnavailableViewer />
        ) : !attachmentData.content && !attachmentData.url ? (
          <Shimmer height={800} />
        ) : formatInfo?.format === 'pdf' ? (
          <PdfViewer
            attachment={attachmentData}
            scale={scale}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
          />
        ) : formatInfo?.format === 'image' ? (
          <ImageViewer attachment={attachmentData} />
        ) : formatInfo?.format === 'text' ? (
          <TextViewer attachment={attachmentData} />
        ) : formatInfo?.format === 'html' ? (
          <HtmlViewer attachment={attachmentData} />
        ) : formatInfo?.format === 'document' ||
          formatInfo?.format === 'sheet' ? (
          <DocumentViewer attachment={attachmentData} />
        ) : (
          <UnsupportedViewer attachment={attachmentData} />
        )}
      </div>
    </div>
  )
}
